import Link from 'next/link'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import type { NonFsdsGeneralV1CourseItem } from '@/types/non-fsds-general-v1.type'

interface CourseDetailDialogProps {
  course: NonFsdsGeneralV1CourseItem | null
  onOpenChange: (open: boolean) => void
  portalContainer?: HTMLElement | null
}

export function CourseDetailDialog({ course, onOpenChange, portalContainer }: CourseDetailDialogProps) {
  const detailTitle = course?.detailTitle || course?.name || ''

  return (
    <Dialog open={Boolean(course)} onOpenChange={onOpenChange}>
      {course && (
        <DialogContent
          portalContainer={portalContainer}
          className="max-w-2xl rounded-[var(--gv1-radius-card)] border-slate-200 bg-white p-8"
        >
          <DialogHeader>
            <div className="mb-3 flex flex-wrap gap-2">
              {course.keywords.slice(0, 3).map(keyword => (
                <span key={keyword} className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-[var(--gv1-accent)]">
                  {keyword}
                </span>
              ))}
            </div>
            <DialogTitle className="text-3xl text-[var(--gv1-text-heading)]">
              {detailTitle}
            </DialogTitle>
            <DialogDescription className="flex flex-wrap gap-x-5 gap-y-2 pt-2 text-sm text-[var(--gv1-text-body)]">
              <span>{course.courseCode}</span>
              <span>{course.timeline}</span>
              <span>{course.level}</span>
            </DialogDescription>
          </DialogHeader>

          {course.detailDescription && (
            <p className="text-sm leading-6 text-[var(--gv1-text-body)]">
              {course.detailDescription}
            </p>
          )}

          <div className="rounded-2xl bg-slate-50 p-5">
            <h4 className="font-semibold text-[var(--gv1-text-heading)]">
              {course.highlightsTitle || 'What you will achieve'}
            </h4>
            <ul className="mt-3 space-y-3">
              {course.highlights.map(highlight => (
                <li key={highlight} className="flex items-start gap-3 text-sm text-[var(--gv1-text-body)]">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs text-[var(--gv1-accent)]">✓</span>
                  {highlight}
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter className="gap-3 sm:space-x-0">
            {course.contactUrl && (
              <Link href={course.contactUrl} className="inline-flex h-11 items-center justify-center rounded-full border border-slate-200 px-6 text-sm font-medium text-[var(--gv1-text-body)] hover:bg-slate-50">
                {course.contactLabel || 'Contact an advisor'}
              </Link>
            )}
            {course.detailUrl && (
              <Link href={course.detailUrl} className="inline-flex h-11 items-center justify-center rounded-full bg-[var(--gv1-accent)] px-6 text-sm font-medium text-white hover:bg-blue-600">
                {course.detailLabel || 'Open course page'}
              </Link>
            )}
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  )
}
